import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  FilterQueryType,
  FilterType,
  FiltersList,
  RangeType,
} from "types/page-layout-types";
import GetExcelBtn from "components/page-layout/get-excel";
import { IconX, IconPlus, IconTrash } from "@tabler/icons-react";
import NavbarContainer from "components/navbar";
import TableSkeleton from "components/tables/table-skeleton";
import CountSkeleton from "components/tables/count-skeleton";
import BlankSearch from "components/blank-pages/blank-search";
import formatDate from "components/helpers/format-date";
import AppSelect from "../ui/AppSelect";

export default function PageLayout({
  children,
  isLoading,
  filters,
  filtersList,
  appendFilter,
  clearFilters,
  search,
  searchAPI,
  customHeader,
  header,
  subheaderText,
  subheaderCount,
  dataLength,
  buttonText,
  buttonModal,
  dateRange,
  rangeNames,
  getExcel,
  excelParams,
  excelCount,
}: {
  children: React.JSX.Element;
  isLoading: boolean;
  filters: boolean;
  filtersList?: FiltersList;
  appendFilter?: (name: string, value: string) => void;
  clearFilters?: (param?: string) => Promise<any>;
  search: boolean;
  searchAPI?: () => void;
  customHeader?: React.JSX.Element;
  header?: string | React.JSX.Element;
  subheaderText?: string;
  subheaderCount?: number;
  dataLength?: number;
  buttonText?: string;
  buttonModal?: string;
  dateRange: boolean;
  rangeNames?: RangeType;
  getExcel?: boolean;
  excelParams?: URLSearchParams;
  excelCount?: number;
}) {
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [filterQuery, setFilterQuery] = useState<FilterQueryType>({});
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const handleFilterChange = (value: string, filter: FilterType[]) => {
    const filterName = filter[0]?.filter_name;
    if (!filterName) return;

    setFilterQuery((prevFilterQuery: FilterQueryType) => ({
      ...prevFilterQuery,
      [filterName]: value,
    }));

    appendFilter && appendFilter(filterName, value);
    startSearch();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setSearchQuery(inputValue);
    appendFilter && appendFilter("search", inputValue);

    if (timeoutId) clearTimeout(timeoutId);

    const newTimeoutId = window.setTimeout(() => {
      startSearch();
    }, 1000);

    setTimeoutId(newTimeoutId);
  };

  const handleDateChange = (param: string, value: string) => {
    switch (param) {
      case rangeNames?.from:
        setStartDate(value);
        break;
      case rangeNames?.to:
        setEndDate(value);
        break;
    }
    appendFilter && appendFilter(param, value);
  };

  const handleClearFilters = async () => {
    await clearFilters?.();
    setFilterQuery({});
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    startSearch();
  };

  const handleClearSearch = () => {
    clearFilters && clearFilters("search");
    setSearchQuery("");
    startSearch();
  };

  const startSearch = useCallback(() => {
    searchAPI && searchAPI();
  }, [searchAPI]);

  const hasActiveFilters: boolean = useMemo(() => {
    return Object.keys(filterQuery).length > 0;
  }, [filterQuery]);

  useEffect(() => {
    startDate && endDate && startSearch();
  }, [startDate, endDate, startSearch]);

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const headerFilters = (
    <div className="d-flex flex-grow-1 flex-lg-grow-0 gap-2">
      {filtersList &&
        Object.values(filtersList).map((filter) => {
          const filterName = filter[0]?.filter_name;
          if (!filterName) return null;

          if (filter[0]?.type === "App") {
            return (
              <AppSelect
                key={filterName}
                list={filter}
                defaultOption={filter[0]}
                handleFilterChange={handleFilterChange}
                filterQuery={filterQuery}
              />
            );
          }

          return (
            <select
              key={filterName}
              className="form-select"
              value={filterQuery[filterName] || ""}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                handleFilterChange(e.target.value, filter)
              }
              aria-label={`Фильтр: ${filterName}`}
            >
              {filter.map((option) => (
                <option
                  key={`${filterName}:${option.value}`}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.name}
                </option>
              ))}
            </select>
          );
        })}
    </div>
  );

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    startSearch();
  };

  const headerSearchInput = (
    <form autoComplete="off" onSubmit={handleSearchSubmit}>
      <div className="flex-grow-1 flex-lg-grow-0">
        <div className="input-group input-group-flat">
          <input
            className="form-control d-inline-block"
            placeholder="Поиск"
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            autoComplete="off"
            aria-label="Поиск"
          />
          <span className="input-group-text">
            {searchQuery && (
              <button
                type="button"
                className="btn btn-link link-secondary p-0"
                onClick={handleClearSearch}
                aria-label="Очистить поиск"
              >
                <IconX className="icon" />
              </button>
            )}
          </span>
        </div>
      </div>
    </form>
  );

  const headerDateRange = (
    <div className="d-flex flex-grow-1 flex-lg-grow-0 gap-2">
      <div className="col input-group input-group-flat">
        <span className="input-group-text pe-1">С</span>
        <input
          type="date"
          className="form-control ps-0"
          max={endDate || formatDate(new Date())}
          value={startDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleDateChange(rangeNames?.from || "", e.target.value)
          }
        />
      </div>
      <div className="col input-group input-group-flat">
        <span className="input-group-text pe-1">По</span>
        <input
          type="date"
          className="form-control ps-0"
          min={startDate}
          max={formatDate(new Date())}
          value={endDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleDateChange(rangeNames?.to || "", e.target.value)
          }
        />
      </div>
    </div>
  );

  const headerButtons = (searchQuery ||
    hasActiveFilters ||
    startDate ||
    endDate ||
    buttonText ||
    getExcel) && (
    <div className="d-flex flex-wrap gap-2">
      {(searchQuery || hasActiveFilters || startDate || endDate) && (
        <button
          className="btn btn-text text-danger"
          onClick={handleClearFilters}
          type="button"
        >
          <IconTrash className="icon" />
          Очистить фильтры
        </button>
      )}
      {buttonText && (
        <button
          className="btn btn-primary"
          data-bs-toggle="modal"
          data-bs-target={buttonModal}
          type="button"
        >
          <IconPlus className="icon" />
          {buttonText}
        </button>
      )}
      {getExcel && (
        <GetExcelBtn
          params={excelParams || new URLSearchParams()}
          count={excelCount || 0}
        />
      )}
    </div>
  );

  return (
    <div className="page">
      <NavbarContainer />
      <div className="page-wrapper">
        {header && (
          <div className="page-header d-print-none">
            <div className="container-xl">
              <div className="row gap-2 align-items-center">
                <div className="col-auto">
                  <h2 className="page-title d-block">{header}</h2>
                  <div className="text-muted mt-1">
                    {subheaderText || ""}
                    {isLoading ? <CountSkeleton /> : subheaderCount || 0}
                  </div>
                </div>

                {(search || buttonText || dateRange) && (
                  <div className="d-flex flex-grow-1 flex-wrap justify-content-end gap-2 col-auto">
                    {filters && headerFilters}
                    {search && headerSearchInput}
                    {dateRange && headerDateRange}
                    {headerButtons}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!isLoading && customHeader && customHeader}

        <div className="page-body">
          <div className="container-xl">
            {isLoading ? (
              <TableSkeleton />
            ) : dataLength ? (
              children
            ) : (
              <BlankSearch />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
